// src/pages/Checkout.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/checkout.css";

import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { IoCubeOutline, IoCardOutline, IoDocumentTextOutline } from "react-icons/io5";

import {
  getUserCart,
  updateCartProduct,
  deleteCartProduct,
  deleteUserCart,
  createAnOrder,
  createDpoPaymentToken,
} from "../app/features/user/userSlice";

// ✅ Print prices list
import { getPprices } from "../app/features/printPrice/printPriceSlice";

import product1 from "../images/products/stamp.jpg";
import dpo from "../images/logo/pay.png";
//import momo from "../images/logo/mobile-mtn.jpg";

// ✅ DELIVERY LOCATION
import { DELIVERY_REGIONS, getSubRegions, computeShipping } from "../config/deliveryLocations";

/* =========================
   ✅ HELPERS
========================= */
const formatUGX = (n) => `UGX ${Number(n || 0).toLocaleString()}`;

/** Size label cleaner */
const stripMeasurements = (val) => {
  const s = String(val || "").trim();
  if (!s) return "";

  let out = s.replace(/\s*\([^)]*\b(mm|cm|in|inch|inches)\b[^)]*\)\s*$/i, "");
  out = out.replace(/\s*\d+\s*(?:×|x)\s*\d+\s*(?:mm|cm|in|inch|inches)\s*$/i, "");
  out = out.replace(/\s*\b(mm|cm|in|inch|inches)\b\s*$/i, "");
  out = out.replace(/\s+/g, " ").trim();
  return out;
};

const getSizeLabel = (sizeValue) => {
  if (!sizeValue) return "";
  if (typeof sizeValue === "object") {
    const pick = sizeValue?.title ?? sizeValue?.name ?? sizeValue?.label ?? sizeValue?.size ?? "";
    return stripMeasurements(pick);
  }
  return stripMeasurements(sizeValue);
};

/** Bulk discount logic */
const getSizePriceByQtyDisplay = (sizeObj, qty = 1, basePrice = 0) => {
  const base = Number(basePrice || 0);
  const q = Math.max(1, Number(qty || 1));

  if (!sizeObj || typeof sizeObj !== "object") {
    return { current: base, old: null, source: "base", isBulkDiscount: false };
  }

  const price = sizeObj?.price;
  const discountPrice = sizeObj?.discountPrice;
  const discountMinQty = sizeObj?.discountMinQty;
  const adj = sizeObj?.priceAdjustment;

  const priceNum = Number(price);
  const hasPrice =
    price !== null &&
    price !== undefined &&
    String(price).trim() !== "" &&
    Number.isFinite(priceNum) &&
    priceNum > 0;

  const hasDiscount =
    discountPrice !== null &&
    discountPrice !== undefined &&
    String(discountPrice).trim() !== "" &&
    Number.isFinite(Number(discountPrice));

  const hasMinQty =
    discountMinQty !== null &&
    discountMinQty !== undefined &&
    String(discountMinQty).trim() !== "" &&
    Number.isFinite(Number(discountMinQty));

  const baseCurrent = hasPrice ? Number(price) : base + Number(adj || 0);

  const bulkApplies = hasDiscount && hasMinQty && q >= Number(discountMinQty);
  if (bulkApplies) {
    const bulk = Number(discountPrice);
    const old = baseCurrent > bulk ? baseCurrent : null;
    return {
      current: bulk,
      old,
      source: "size.bulkDiscount",
      isBulkDiscount: true,
    };
  }

  return {
    current: baseCurrent,
    old: null,
    source: hasPrice ? "size.price" : "size.adjustment",
    isBulkDiscount: false,
  };
};


const COD_MAX_TOTAL = 500000;

const hasFileUploadTag = (product) => {
  const tags = Array.isArray(product?.tags) ? product.tags : [];

  return tags.some((tag) => {
    const value =
      typeof tag === "string"
        ? tag
        : tag?.title || tag?.name || tag?.label || "";

    const clean = String(value)
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

    return ["file upload", "featured file upload"].includes(clean);
  });
};

const getProductPriceByQtyDisplay = (qty = 1, baseCurrent = 0, baseOld = null, product = null) => {
  const q = Math.max(1, Number(qty || 1));

  const minRaw =
    product?.bulkDiscount?.minQty ??
    product?.bulkDiscountMinQty ??
    product?.bulkMinQty ??
    product?.bulkDiscountQty ??
    null;

  const priceRaw =
    product?.bulkDiscount?.price ??
    product?.bulkDiscountPrice ??
    product?.bulkPrice ??
    product?.bulkDiscountUnitPrice ??
    null;

  const min = minRaw !== "" && minRaw !== null && minRaw !== undefined ? Number(minRaw) : null;
  const disc = priceRaw !== "" && priceRaw !== null && priceRaw !== undefined ? Number(priceRaw) : null;

  const hasBulk = Number.isFinite(min) && min >= 1 && Number.isFinite(disc) && disc >= 0;

  if (hasBulk && q >= min) {
    const old = Number(baseCurrent) > disc ? Number(baseCurrent) : null;
    return {
      current: disc,
      old: old || baseOld || null,
      source: "product.bulkDiscount",
      isBulkDiscount: true,
      bulkMinQty: min,
    };
  }

  return {
    current: baseCurrent,
    old: baseOld || null,
    source: "product.base",
    isBulkDiscount: false,
    bulkMinQty: hasBulk ? min : null,
  };
};

/** ✅ COLOR PRICE LOGIC */
const getColorPriceByQtyDisplay = (product, colorId, qty = 1) => {
  const q = Math.max(1, Number(qty || 1));
  const p = product || {};

  const basePrice = Number(p?.price ?? 0);

  const productDiscountedPrice =
    p?.discountedPrice !== null &&
    p?.discountedPrice !== undefined &&
    p?.discountedPrice !== ""
      ? Number(p.discountedPrice)
      : null;

  const productDiscountMinQty =
    p?.discountMinQty !== null &&
    p?.discountMinQty !== undefined &&
    p?.discountMinQty !== ""
      ? Number(p.discountMinQty)
      : null;

  const productBulkMinQty =
    p?.bulkDiscount?.minQty !== null &&
    p?.bulkDiscount?.minQty !== undefined &&
    p?.bulkDiscount?.minQty !== ""
      ? Number(p.bulkDiscount.minQty)
      : null;

  const productBulkPrice =
    p?.bulkDiscount?.price !== null &&
    p?.bulkDiscount?.price !== undefined &&
    p?.bulkDiscount?.price !== ""
      ? Number(p.bulkDiscount.price)
      : null;

  const variants = Array.isArray(p?.colorVariants) ? p.colorVariants : [];

  const variant =
    colorId != null
      ? variants.find((v) => String(v?.color?._id || v?.color) === String(colorId))
      : null;

  const finalBasePrice =
    variant?.price !== null &&
    variant?.price !== undefined &&
    variant?.price !== ""
      ? Number(variant.price)
      : basePrice;

  const finalDiscountedPrice =
    variant?.discountedPrice !== null &&
    variant?.discountedPrice !== undefined &&
    variant?.discountedPrice !== ""
      ? Number(variant.discountedPrice)
      : productDiscountedPrice !== null && productDiscountedPrice < finalBasePrice
      ? productDiscountedPrice
      : null;

  const finalDiscountMinQty =
    finalDiscountedPrice !== null
      ? variant?.discountMinQty !== null &&
        variant?.discountMinQty !== undefined &&
        variant?.discountMinQty !== ""
        ? Number(variant.discountMinQty)
        : productDiscountMinQty
      : null;

  const finalBulkPrice =
    variant?.bulkDiscount?.price !== null &&
    variant?.bulkDiscount?.price !== undefined &&
    variant?.bulkDiscount?.price !== ""
      ? Number(variant.bulkDiscount.price)
      : productBulkPrice !== null && productBulkPrice <= finalBasePrice
      ? productBulkPrice
      : null;

  const finalBulkMinQty =
    finalBulkPrice !== null
      ? variant?.bulkDiscount?.minQty !== null &&
        variant?.bulkDiscount?.minQty !== undefined &&
        variant?.bulkDiscount?.minQty !== ""
        ? Number(variant.bulkDiscount.minQty)
        : productBulkMinQty
      : null;

  if (
    finalBulkMinQty !== null &&
    Number.isFinite(finalBulkMinQty) &&
    finalBulkPrice !== null &&
    Number.isFinite(finalBulkPrice) &&
    q >= finalBulkMinQty
  ) {
    return {
      current: finalBulkPrice,
      old: finalBasePrice > finalBulkPrice ? finalBasePrice : null,
      source: "color.bulkDiscount",
      isBulkDiscount: true,
      discountMinQty: finalBulkMinQty,
      variant,
    };
  }

  if (
    finalDiscountedPrice !== null &&
    Number.isFinite(finalDiscountedPrice) &&
    finalDiscountMinQty !== null &&
    Number.isFinite(finalDiscountMinQty) &&
    q >= finalDiscountMinQty
  ) {
    return {
      current: finalDiscountedPrice,
      old: finalBasePrice > finalDiscountedPrice ? finalBasePrice : null,
      source: "color.discount",
      isBulkDiscount: false,
      discountMinQty: finalDiscountMinQty,
      variant,
    };
  }

  return {
    current: finalBasePrice,
    old: null,
    source: variant?.price != null ? "color.price" : "product.base",
    isBulkDiscount: false,
    discountMinQty: null,
    variant,
  };
};

/** Images */
const normalizeImg = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  return val?.url || "";
};

const getFirstProductImage = (p) => {
  if (!p) return "";
  if (Array.isArray(p?.images) && p.images.length) return normalizeImg(p.images[0]);
  return normalizeImg(p?.image);
};

const getVariantImageByColor = (productObj, colorId) => {
  if (!productObj || !colorId) return "";
  const list = Array.isArray(productObj?.variantImages) ? productObj.variantImages : [];
  const hit = list.find((v) => String(v?.color?._id || v?.color || "") === String(colorId));
  return hit?.images?.[0]?.url || "";
};

const pickCartItemImage = (cartItem, productObj) => {
  const fromCart = normalizeImg(cartItem?.variantImage);
  if (fromCart) return fromCart;

  const colorId = cartItem?.color?._id || cartItem?.color || "";
  const fromVariantMap = getVariantImageByColor(productObj, colorId);
  if (fromVariantMap) return fromVariantMap;

  const fromProd = getFirstProductImage(productObj);
  return fromProd || product1;
};

/* =========================
   ✅ PICKUP STATIONS
========================= */
const PICKUP_STATIONS = ["Kupto Pick Up Station (Main)"];

/* =========================
   ✅ PAYMENT METHODS
========================= */
const PAYMENT_METHODS = {
  cashOnDelivery: {
    value: "cashOnDelivery",
    label: "Cash on Delivery",
    active: true,
  },
  dpo: {
    value: "dpo",
    label: "Pay Online",
    active: true,
  },
};

const isPaymentMethodActive = (method) => Boolean(PAYMENT_METHODS?.[method]?.active);

/* =========================
   ✅ SAVE DELIVERY ADDRESS
========================= */
const DELIVERY_SAVE_KEY = "kupto_delivery_address_v1";

const getDeliveryStorageKey = (user) => {
  const uid = user?._id || user?.id || "";
  const email = user?.email || "";
  const slug = uid ? `uid:${uid}` : email ? `em:${email}` : "logged";
  return `${DELIVERY_SAVE_KEY}__${slug}`;
};

const readSavedDelivery = (user) => {
  try {
    const key = getDeliveryStorageKey(user);
    const raw = JSON.parse(localStorage.getItem(key) || "null");
    if (!raw || typeof raw !== "object") return null;

    const address = String(raw?.address || "").trim();
    const region = String(raw?.region || "").trim();
    const subRegion = String(raw?.subRegion || "").trim();

    if (!address || !region) return null;
    return { address, region, subRegion };
  } catch {
    return null;
  }
};

const writeSavedDelivery = (user, payload) => {
  try {
    const key = getDeliveryStorageKey(user);
    const safe = {
      address: String(payload?.address || "").trim(),
      region: String(payload?.region || "").trim(),
      subRegion: String(payload?.subRegion || "").trim(),
      savedAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(safe));
    return true;
  } catch {
    return false;
  }
};

const clearSavedDelivery = (user) => {
  try {
    const key = getDeliveryStorageKey(user);
    localStorage.removeItem(key);
  } catch {}
};

/* =========================
   ✅ SKELETON HELPERS
========================= */
const CartItemSkeleton = ({ k }) => (
  <div className="co-cartItem co-skelItem" key={k}>
    <div className="co-cartThumb co-skelThumb" />
    <div className="co-cartInfo">
      <div className="co-skelLine co-skelLine--lg" />
      <div className="co-skelLine co-skelLine--sm" />
      <div className="co-skelLine co-skelLine--md" />
    </div>
    <div className="co-cartControls">
      <div className="co-skelBtn" />
      <div className="co-skelQty" />
      <div className="co-skelBtn" />
      <div className="co-skelX" />
    </div>
  </div>
);

const PriceSkeleton = () => (
  <div className="co-priceSkel">
    <div className="co-skelRow">
      <span className="co-skelLine co-skelLine--md" />
      <span className="co-skelLine co-skelLine--sm" />
    </div>
    <div className="co-skelRow">
      <span className="co-skelLine co-skelLine--md" />
      <span className="co-skelLine co-skelLine--sm" />
    </div>
    <div className="co-divider" />
    <div className="co-skelRow">
      <span className="co-skelLine co-skelLine--md" />
      <span className="co-skelLine co-skelLine--md" />
    </div>
  </div>
);

const FormSkeleton = () => (
  <div className="co-formSkel">
    <div className="co-skelGrid2">
      <div className="co-skelInput" />
      <div className="co-skelInput" />
    </div>
    <div className="co-skelGrid2">
      <div className="co-skelInput" />
      <div className="co-skelInput" />
    </div>
    <div className="co-skelInput" />
    <div className="co-skelInput" />
    <div className="co-skelGrid2">
      <div className="co-skelInput" />
      <div className="co-skelInput" />
    </div>
    <div className="co-skelTextarea" />
    <div className="co-skelBtnRow">
      <div className="co-skelCta" />
      <div className="co-skelCta" />
    </div>
  </div>
);

const Checkout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authState = useSelector((state) => state?.auth || {});
  const user = authState?.user || JSON.parse(localStorage.getItem("user") || "null");
 
  const isLoggedIn = Boolean(user?.token || localStorage.getItem("token"));

  const { cartProduct, isLoading, isError, message } = useSelector((state) => state.auth);

  const printPriceState = useSelector((state) => state?.printPrice || state?.printPrices || {});
  const printPrices = useMemo(() => {
    const arr = printPriceState?.printPrices;
    return Array.isArray(arr) ? arr : [];
  }, [printPriceState?.printPrices]);

  useEffect(() => {
    dispatch(getPprices());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoggedIn) {
      toast.info("Please login to continue checkout.");
      navigate("/login");
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (isLoggedIn) dispatch(getUserCart());
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (isError && message) toast.error(message);
  }, [isError, message]);

  const [step, setStep] = useState(1);
  const [mobileStep, setMobileStep] = useState(1);

  /* =========================
     ✅ PRINT PRICING RESOLVER
  ========================== */
  const resolvePrintPricing = useCallback(
    (p) => {
      if (!p) return null;

      const directObj =
        p?.printingPrice &&
        typeof p.printingPrice === "object" &&
        (p.printingPrice?.title || p.printingPrice?.printPrice)
          ? p.printingPrice
          : p?.printPricing &&
            typeof p.printPricing === "object" &&
            (p.printPricing?.title || p.printPricing?.printPrice)
          ? p.printPricing
          : p?.printPrice &&
            typeof p.printPrice === "object" &&
            (p.printPrice?.title || p.printPrice?.printPrice)
          ? p.printPrice
          : null;

      const idCandidates = [
        typeof p?.printingPrice === "string" ? p.printingPrice : null,
        typeof p?.printingPriceId === "string" ? p.printingPriceId : null,
        typeof p?.printPricingId === "string" ? p.printPricingId : null,
        typeof p?.printPrice === "string" ? p.printPrice : null,
        typeof p?.printPriceId === "string" ? p.printPriceId : null,
        typeof p?.pprice === "string" ? p.pprice : null,
        typeof p?.ppriceId === "string" ? p.ppriceId : null,
      ].filter(Boolean);

      const titleCandidates = [
        p?.printingType,
        p?.printType,
        p?.printPriceTitle,
        p?.printPricingTitle,
        p?.printPriceName,
        p?.printingPriceTitle,
      ]
        .map((x) => (x == null ? "" : String(x)))
        .map((x) => x.trim())
        .filter(Boolean);

      const byId = (id) => {
        if (!id) return null;
        return (printPrices || []).find((x) => String(x?._id) === String(id)) || null;
      };

      const byTitle = (t) => {
        const s = String(t || "").trim().toLowerCase();
        if (!s) return null;
        return (printPrices || []).find((x) => String(x?.title || "").trim().toLowerCase() === s) || null;
      };

      let record = directObj;

      if (!record) {
        for (const id of idCandidates) {
          record = byId(id);
          if (record) break;
        }
      }

      if (!record) {
        for (const t of titleCandidates) {
          record = byTitle(t);
          if (record) break;
        }
      }

      if (!record) return null;

      const preparePrice = Number(record?.preparePrice ?? 0) || 0;

      const oneSide = Number(record?.printPrice?.oneSide ?? 0) || 0;
      const twoSide = Number(record?.printPrice?.twoSide ?? 0) || 0;

      const discOne = Number(record?.printPriceDiscount?.oneSide ?? 0) || 0;
      const discTwo = Number(record?.printPriceDiscount?.twoSide ?? 0) || 0;

      const discountMinQty =
        record?.discountMinQty !== undefined && record?.discountMinQty !== null
          ? Number(record.discountMinQty)
          : null;

      return {
        id: record?._id || null,
        title: record?.title || "",
        preparePrice,
        discountMinQty: Number.isFinite(discountMinQty) ? discountMinQty : null,
        printPrice: { oneSide, twoSide },
        printPriceDiscount: { oneSide: discOne, twoSide: discTwo },
      };
    },
    [printPrices]
  );

  const getPrintKey = useCallback((pricing) => {
    if (!pricing) return "";
    const recId = pricing?.id ? String(pricing.id) : "";
    const recTitle = pricing?.title ? String(pricing.title) : "";
    return recId ? `pprice:${recId}` : recTitle ? `ppriceTitle:${recTitle.toLowerCase()}` : "";
  }, []);

  const getPaymentImage = useCallback((method) => {
  if (method === "dpo") return dpo;
  return "";
}, []);

  /* =========================
     ✅ FORM
  ========================== */
  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    address: "",
    region: "",
    subRegion: "",
    note: "",
    paymentMethod: "dpo",
    deliveryMethod: "delivery",
    pickupStation: PICKUP_STATIONS?.[0] || "",
  });

  const [saveDelivery, setSaveDelivery] = useState(true);
  const [editIdentity, setEditIdentity] = useState(false);
  const [editDelivery, setEditDelivery] = useState(false);

  const isPickup = useMemo(() => String(form.deliveryMethod || "") === "pickup", [form.deliveryMethod]);

  useEffect(() => {
  const handleFocus = (e) => {
    const target = e.target;

    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "SELECT"
    ) {
      setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 350);
    }
  };

  document.addEventListener("focusin", handleFocus);

  return () => {
    document.removeEventListener("focusin", handleFocus);
  };
}, []);

  useEffect(() => {
  if (!isLoggedIn) return;

  const firstname =
    user?.firstname ||
    user?.firstName ||
    user?.fname ||
    "";

  const lastname =
    user?.lastname ||
    user?.lastName ||
    user?.lname ||
    "";

  const phone =
    user?.mobile ||
    user?.phone ||
    user?.phoneNo ||
    user?.telephone ||
    "";

  const email =
    user?.email ||
    "";

  setForm((p) => ({
    ...p,
    firstname: p.firstname || firstname,
    lastname: p.lastname || lastname,
    phone: p.phone || phone,
    email: p.email || email,
  }));
}, [isLoggedIn, user]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (isPickup) return;

    const saved = readSavedDelivery(user);
    if (!saved) return;

    setForm((p) => ({
      ...p,
      address: p.address ? p.address : saved.address,
      region: p.region ? p.region : saved.region,
      subRegion: p.subRegion ? p.subRegion : saved.subRegion,
    }));

    setEditDelivery(false);
  }, [isLoggedIn, user, isPickup]);



  const isIdentityFilled = useMemo(() => {
    const fn = String(form.firstname || "").trim();
    const ln = String(form.lastname || "").trim();
    const ph = String(form.phone || "").trim();
    const em = String(form.email || "").trim();
    return Boolean(fn && ln && ph && em);
  }, [form.firstname, form.lastname, form.phone, form.email]);

  useEffect(() => {
    if (isIdentityFilled) setEditIdentity(false);
  }, [isIdentityFilled]);

  const subRegions = useMemo(() => getSubRegions(form.region), [form.region]);

  const isDeliveryFilled = useMemo(() => {
    if (isPickup) {
      return Boolean(String(form.pickupStation || "").trim());
    }

    const ad = String(form.address || "").trim();
    const rg = String(form.region || "").trim();
    const sr = String(form.subRegion || "").trim();

    if (!ad || !rg) return false;

    const needsSub = Array.isArray(subRegions) && subRegions.length > 0;
    if (needsSub) return Boolean(sr);

    return true;
  }, [isPickup, form.pickupStation, form.address, form.region, form.subRegion, subRegions]);

  const deliveryFocusedRef = useRef(false);
  const blurTimerRef = useRef(null);

  const onDeliveryFocus = useCallback(() => {
    deliveryFocusedRef.current = true;
    setEditDelivery(true);
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  }, []);

  const onDeliveryBlur = useCallback(() => {
    deliveryFocusedRef.current = false;
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);

    blurTimerRef.current = setTimeout(() => {
      if (!deliveryFocusedRef.current && isDeliveryFilled) {
        setEditDelivery(false);
      }
    }, 180);
  }, [isDeliveryFilled]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isPickup) return;

    if (!form.region && form.subRegion) {
      setForm((p) => ({ ...p, subRegion: "" }));
      return;
    }

    if (form.region && form.subRegion && !subRegions.includes(form.subRegion)) {
      setForm((p) => ({ ...p, subRegion: "" }));
    }
  }, [isPickup, form.region, form.subRegion, subRegions]);

  /* =========================
     ✅ NORMALIZE CART ITEMS
  ========================== */
  const items = useMemo(() => {
    const raw = Array.isArray(cartProduct) ? cartProduct : Array.isArray(cartProduct?.cart) ? cartProduct.cart : [];

    return raw
      .map((c) => {
        const cartItemId = c?._id;
        const p = c?.productId;

        if (!cartItemId) return null;

        const qty = Math.max(1, Number(c?.quantity ?? 1) || 1);

        const colorId = c?.color?._id || c?.color || null;

        const colorPrice =
          typeof p === "object"
            ? getColorPriceByQtyDisplay(p, colorId, qty)
            : {
                current: 0,
                old: null,
                source: "product.base",
                isBulkDiscount: false,
                discountMinQty: null,
                variant: null,
              };

        const baseCurrent = Number(colorPrice?.current ?? 0);
        const baseOld = colorPrice?.old ?? null;

        const sizeObj = c?.size && typeof c.size === "object" ? c.size : null;
        const sizePrice = sizeObj ? getSizePriceByQtyDisplay(sizeObj, qty, baseCurrent) : null;

        let unitProduct = sizeObj ? Number(sizePrice?.current ?? baseCurrent) : baseCurrent;
        let oldProduct = sizeObj ? sizePrice?.old ?? baseOld ?? null : baseOld;

        if (sizeObj && sizePrice && !sizePrice.isBulkDiscount && sizePrice.source !== "size.price") {
          const prodPrice = getProductPriceByQtyDisplay(qty, sizePrice.current, baseOld, p);
          unitProduct = prodPrice.current;
          oldProduct = prodPrice.old ?? oldProduct;
        } else if (!sizeObj) {
          const prodPrice = getProductPriceByQtyDisplay(qty, baseCurrent, baseOld, p);
          unitProduct = prodPrice.current;
          oldProduct = prodPrice.old ?? oldProduct;
        }

        const pricing = typeof p === "object" ? resolvePrintPricing(p) : null;
        const hasPrintPricing = Boolean(pricing?.title);
        const printSide = String(c?.printSide || "").trim();

        const printMin = Number(pricing?.discountMinQty);
        const printDiscApplies =
          hasPrintPricing && Number.isFinite(printMin) && printMin >= 1 ? qty >= printMin : false;

        const printBase = hasPrintPricing && printSide ? Number(pricing?.printPrice?.[printSide] ?? 0) || 0 : 0;

        const printDisc =
          hasPrintPricing && printSide ? Number(pricing?.printPriceDiscount?.[printSide] ?? 0) || 0 : 0;

        const printUnit = printDiscApplies && printDisc > 0 ? printDisc : printBase;

        const unitPrice = Number(unitProduct || 0) + Number(printUnit || 0);
        const oldUnitPrice = oldProduct;

        const productId = (typeof p === "object" ? p?._id : null) || c?.productId;
        const name = (typeof p === "object" ? p?.title || p?.name : null) || "Untitled";

        const color =
          c?.color?.title ||
          c?.color?.name ||
          c?.color?.label ||
          c?.color?.color ||
          (typeof c?.color === "string" ? c.color : "");

        const sizeLabel = getSizeLabel(c?.size);
        const img = pickCartItemImage(c, typeof p === "object" ? p : null);

        const printPricingTitle = String(c?.printPricingTitle || pricing?.title || "").trim();
        const printKey = String(c?.printKey || getPrintKey(pricing) || "").trim();
        const preparePriceOnce = Number(c?.preparePriceOnce ?? 0) || 0;
        const prepareCandidate =
          preparePriceOnce > 0 ? preparePriceOnce : Number(pricing?.preparePrice ?? 0) || 0;

        return {
          source: "backend",
          raw: c,

          cartItemId,
          productId: String(productId || ""),
          name,
          qty,
          unitPrice,
          oldUnitPrice,
          img,

          color: String(color || ""),
          size: String(sizeLabel || ""),

          hasPrintPricing,
          printSide,
          printUnitPrice: Number(printUnit || 0),
          printPricingTitle,
          printDiscountMinQty: pricing?.discountMinQty ?? null,

          printKey,
          preparePriceOnce: Number(prepareCandidate || 0),
        };
      })
      .filter(Boolean);
  }, [cartProduct, resolvePrintPricing, getPrintKey]);

  const setupFeeTotal = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const seen = new Set();
    let sum = 0;

    for (const it of list) {
      const key = String(it?.printKey || "").trim();
      const fee = Number(it?.preparePriceOnce ?? 0) || 0;

      if (!key || fee <= 0) continue;
      if (seen.has(key)) continue;

      seen.add(key);
      sum += fee;
    }

    return Math.max(0, sum);
  }, [items]);

  const itemsCount = useMemo(() => items.reduce((a, x) => a + (Number(x.qty) || 0), 0), [items]);

  const itemsTotal = useMemo(
    () => items.reduce((sum, x) => sum + (Number(x.unitPrice) || 0) * (Number(x.qty) || 0), 0),
    [items]
  );

  const shipping = useMemo(() => {
    if (!items.length) return 0;
    if (isPickup) return 0;
    return computeShipping(form.region, form.subRegion, true);
  }, [isPickup, form.region, form.subRegion, items.length]);

  const total = useMemo(() => Math.max(0, itemsTotal + setupFeeTotal + shipping), [itemsTotal, setupFeeTotal, shipping]);



  const hasUploadTaggedItem = useMemo(() => {
  return items.some((item) => {
    const product = item?.raw?.productId;
    return product && typeof product === "object" && hasFileUploadTag(product);
  });
}, [items]);

const codAllowed = useMemo(() => {
  return total <= COD_MAX_TOTAL && !hasUploadTaggedItem;
}, [total, hasUploadTaggedItem]);

  /* =========================
     ✅ CART CONTROLS
  ========================== */
  const setQty = useCallback(
    async (cartItemId, nextQty) => {
      const qty = Math.max(1, Number(nextQty) || 1);

      try {
        await dispatch(updateCartProduct({ cartItemId, quantity: qty })).unwrap();
        dispatch(getUserCart());
      } catch (err) {
        toast.error(err?.message || "Failed to update quantity");
      }
    },
    [dispatch]
  );

  const incQty = useCallback((id, q) => setQty(id, q + 1), [setQty]);
  const decQty = useCallback((id, q) => setQty(id, Math.max(1, q - 1)), [setQty]);

  const removeItem = useCallback(
    async (cartItemId) => {
      try {
        await dispatch(deleteCartProduct(cartItemId)).unwrap();
        dispatch(getUserCart());
        
      } catch (err) {
        toast.error(err?.message || "Failed to remove item");
      }
    },
    [dispatch]
  );

  const clearCart = useCallback(
    async ({ silent = false } = {}) => {
      try {
        await dispatch(deleteUserCart()).unwrap();
        dispatch(getUserCart());
        if (!silent) toast.success("Cart cleared");
      } catch (err) {
        toast.error(err?.message || "Failed to clear cart");
      }
    },
    [dispatch]
  );

  /* =========================
   ✅ VALIDATION
========================== */
const validateInfo = () => {
  const firstname = String(form.firstname || "").trim();
  const lastname = String(form.lastname || "").trim();
  const phone = String(form.phone || "").trim();
  const email = String(form.email || "").trim();

  if (!items.length) {
    toast.info("Your cart is empty.");
    return false;
  }

  if (!firstname) {
    toast.error("Please enter your first name");
    return false;
  }

  if (!lastname) {
    toast.error("Please enter your last name");
    return false;
  }

  if (!phone) {
    toast.error("Please enter your phone number");
    return false;
  }

  if (!email || !email.includes("@")) {
    toast.error("Please enter a valid email address");
    return false;
  }

  if (isPickup) {
    const st = String(form.pickupStation || "").trim();

    if (!st) {
      toast.error("Please choose a pick up station");
      return false;
    }
  } else {
    const address = String(form.address || "").trim();
    const region = String(form.region || "").trim();
    const subRegion = String(form.subRegion || "").trim();

    if (!address) {
      toast.error("Please enter your delivery address");
      return false;
    }

    if (!region) {
      toast.error("Please choose your delivery location");
      return false;
    }

    const needsSub = Array.isArray(subRegions) && subRegions.length > 0;

    if (needsSub && !subRegion) {
      toast.error("Please choose your sub region");
      return false;
    }
  }

  // ✅ Cash on Delivery limit
  if (form.paymentMethod === "cashOnDelivery" && total > COD_MAX_TOTAL) {
    toast.error("Cash on Delivery is only available for orders below UGX 500,000.");
    return false;
  }

  // ✅ No COD for file upload products
  if (form.paymentMethod === "cashOnDelivery" && hasUploadTaggedItem) {
    toast.error("Cash on Delivery is not available for file upload orders.");
    return false;
  }

  return true;
};

  const placingRef = useRef(false);

  /* =========================
     ✅ ORDER PAYLOAD
  ========================== */
  const buildOrderPayload = useCallback(() => {
    const safePaymentMethod = isPaymentMethodActive(form.paymentMethod) ? form.paymentMethod : "cashOnDelivery";
    const pm = String(safePaymentMethod || "").trim();

    const baseShippingInfo = {
      firstName: String(form.firstname || "").trim(),
      lastName: String(form.lastname || "").trim(),
      phone: String(form.phone || "").trim(),
      email: String(form.email || "").trim(),
    };

    const shippingInfo = isPickup
      ? {
          ...baseShippingInfo,
          address: String(form.pickupStation || "Pick Up Station").trim(),
          region: "Pick Up Station",
          subRegion: "",
          deliveryMethod: "pickup",
          pickupStation: String(form.pickupStation || "").trim(),
        }
      : {
          ...baseShippingInfo,
          address: String(form.address || "").trim(),
          region: String(form.region || "").trim(),
          subRegion: String(form.subRegion || "").trim(),
          deliveryMethod: "delivery",
        };

    const orderItems = (items || []).map((x) => ({
      product: x.productId,
      quantity: Number(x.qty || 1),

      color: x.raw?.color?._id || x.raw?.color || null,
      size: x.raw?.size?._id || x.raw?.size || null,

      unitPrice: Number(x.unitPrice || 0),

      printUnitPrice: Number(x.printUnitPrice || 0),
      printPricingTitle: x.printPricingTitle || null,
      printSide: x.printSide || "",

      instruction: x.raw?.instruction || null,
      uploadedFiles: x.raw?.uploadedFiles || [],
    }));

    return {
      shippingInfo,
      paymentInfo: {
        paymentMethod: pm,
        status: "Pending",
      },
      note: String(form.note || "").trim() || null,

      orderItems,

      itemsTotal: Number(itemsTotal || 0),
      shippingPrice: Number(shipping || 0),
      setupFeeTotal: Number(setupFeeTotal || 0),

      totalPrice: Number(total || 0),
      totalPriceAfterDiscount: Number(total || 0),
    };
  }, [form, items, itemsTotal, setupFeeTotal, shipping, total, isPickup]);


  


  useEffect(() => {
  if (form.paymentMethod === "cashOnDelivery" && !codAllowed) {
    setForm((p) => ({ ...p, paymentMethod: "dpo" }));
    toast.info("Cash on Delivery is not available for this order.");
  }
}, [form.paymentMethod, codAllowed]);


  const placeOrderAndHandlePayment = useCallback(async () => {
  if (!items.length) {
    toast.info("Your cart is empty.");
    return;
  }

  const payload = buildOrderPayload();
  const res = await dispatch(createAnOrder(payload)).unwrap();

  const orderId = res?.order?._id || res?.orderId || res?._id;

  if (!orderId) {
    throw new Error("Order created but order ID was not returned");
  }

  if (form.paymentMethod === "dpo") {
    const dpoRes = await dispatch(createDpoPaymentToken(orderId)).unwrap();

    if (!dpoRes?.paymentUrl) {
      throw new Error("DPO payment link was not generated");
    }

    localStorage.setItem("kupto_dpo_token", dpoRes.token || "");
    localStorage.setItem("kupto_dpo_order_id", orderId);

    window.location.href = dpoRes.paymentUrl;
    return;
  }

  await clearCart({ silent: true });

  toast.success(res?.message || "Order placed successfully");

  localStorage.setItem("lastCompletedOrder", JSON.stringify(res));

  navigate("/thank-you", {
    replace: true,
    state: { completedOrder: res },
  });
}, [
  items.length,
  buildOrderPayload,
  dispatch,
  form.paymentMethod,
  clearCart,
  navigate,
]);

  /* =========================
     ✅ DESKTOP FLOW
  ========================== */
  const onProceed = async (e) => {
    e.preventDefault();
    if (placingRef.current) return;

    if (step === 1) {
      if (!validateInfo()) return;

      if (!isPickup) {
        if (saveDelivery) {
          const ok = writeSavedDelivery(user, {
            address: form.address,
            region: form.region,
            subRegion: form.subRegion,
          });
          if (!ok) toast.info("Could not save delivery address on this device.");
        } else {
          clearSavedDelivery(user);
        }
      }

      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (step === 2) {
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    placingRef.current = true;

    try {
      if (!items.length) {
        toast.info("Your cart is empty.");
        return;
      }

      await placeOrderAndHandlePayment();

    } catch (err) {
      toast.error(err?.message || "Failed to place order");
    } finally {
      placingRef.current = false;
    }
  };

  /* =========================
     ✅ MOBILE FLOW
  ========================== */
  const saveDeliveryIfNeeded = useCallback(() => {
    if (!isPickup) {
      if (saveDelivery) {
        const ok = writeSavedDelivery(user, {
          address: form.address,
          region: form.region,
          subRegion: form.subRegion,
        });
        if (!ok) toast.info("Could not save delivery address on this device.");
      } else {
        clearSavedDelivery(user);
      }
    }
  }, [isPickup, saveDelivery, user, form.address, form.region, form.subRegion]);

  const onMobileNext = async () => {
    if (placingRef.current) return;

    if (mobileStep === 1) {
      if (!validateInfo()) return;
      saveDeliveryIfNeeded();
      setMobileStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (mobileStep === 2) {
      setMobileStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    placingRef.current = true;
    try {
      if (!items.length) {
        toast.info("Your cart is empty.");
        return;
      }

      await placeOrderAndHandlePayment();

    } catch (err) {
      toast.error(err?.message || "Failed to place order");
    } finally {
      placingRef.current = false;
    }
  };

  const goBackStep = () => {
    if (step === 1) {
      navigate(-1);
      return;
    }
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBackMobileStep = () => {
    if (mobileStep === 1) {
      navigate(-1);
      return;
    }
    setMobileStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showSkeleton = Boolean(isLoggedIn && isLoading);

  const fullName = useMemo(() => {
    const fn = String(form.firstname || "").trim();
    const ln = String(form.lastname || "").trim();
    return `${fn} ${ln}`.trim();
  }, [form.firstname, form.lastname]);

  const deliveryLine = useMemo(() => {
    if (isPickup) {
      const st = String(form.pickupStation || "").trim();
      return st ? `Pick Up • ${st}` : "Pick Up Station";
    }
    const a = String(form.address || "").trim();
    const r = String(form.region || "").trim();
    const s = String(form.subRegion || "").trim();
    return [a, r, s].filter(Boolean).join(" • ");
  }, [isPickup, form.pickupStation, form.address, form.region, form.subRegion]);

  const paymentLabel = useMemo(() => {
  const pm = String(form.paymentMethod || "").trim();
  if (pm === "cashOnDelivery") return "Cash on Delivery";
  if (pm === "dpo") return "Pay Online";
  return "—";
}, [form.paymentMethod]);

  const mobileTitle = useMemo(() => {
    if (mobileStep === 1) return "Shipping";
    if (mobileStep === 2) return "Payment";
    return "Summary";
  }, [mobileStep]);

  if (!isLoggedIn) return null;

  return (
    <div className="co-page">
      {/* ======================================================
          ✅ MOBILE / APP UI
      ======================================================= */}
      <div className="co-app">
        <div className="co-appPhoneWrap">
          <div className="co-appPhone">
            <div className="co-appTop">
              <button type="button" className="co-appBack" onClick={goBackMobileStep} aria-label="Back">
                ‹
              </button>
              <div className="co-appTitle">{mobileTitle}</div>
              <div className="co-appSpacer" />
            </div>

            <div className="co-appBody">
              <div className="co-mSteps">
                <div className={`co-mStep ${mobileStep >= 1 ? "active" : ""}`}>
                  <div className="co-mStepIcon">
                    <IoCubeOutline />
                  </div>
                  <div className="co-mStepText">SHIPPING</div>
                </div>

                <div className={`co-mStep ${mobileStep >= 2 ? "active" : ""}`}>
                  <div className="co-mStepIcon">
                    <IoCardOutline />
                  </div>
                  <div className="co-mStepText">PAYMENT</div>
                </div>

                <div className={`co-mStep ${mobileStep >= 3 ? "active" : ""}`}>
                  <div className="co-mStepIcon">
                    <IoDocumentTextOutline />
                  </div>
                  <div className="co-mStepText">SUMMARY</div>
                </div>
              </div>

              {mobileStep === 1 && (
                <>
                  <div className="co-mSectionTitle">Shipping address</div>

                  <div className="co-appCard co-appCard--soft">
                    <div className="co-appForm">
                      {isIdentityFilled && !editIdentity ? (
                        <div className="co-summaryCard">
                          <div className="co-summaryTop">
                            <div className="co-summaryTitle">Contact details</div>
                            <button
                              type="button"
                              className="edit"
                              onClick={() => setEditIdentity(true)}
                              style={{ background: "transparent", cursor: "pointer" }}
                            >
                              Edit
                            </button>
                          </div>

                          <div className="co-summaryBody">
                            <div>
                              <strong>Name:</strong> {fullName || "—"}
                            </div>
                            <div>
                              <strong>Phone:</strong> {form.phone || "—"}
                            </div>
                            <div>
                              <strong>Email:</strong> {form.email || "—"}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="co-sectionHead">
                            <div className="co-sectionTitle">Contact details</div>

                            {isIdentityFilled ? (
                              <button
                                type="button"
                                className="edit"
                                onClick={() => setEditIdentity(false)}
                                style={{ background: "transparent", cursor: "pointer" }}
                              >
                                Hide
                              </button>
                            ) : null}
                          </div>

                          <div className="co-appGrid2">
                            <div className="co-field">
                              <label className="co-label">First name</label>
                              <input
                                className="co-input"
                                value={form.firstname}
                                onChange={(e) => setForm((p) => ({ ...p, firstname: e.target.value }))}
                                placeholder="Enter first name"
                                required
                              />
                            </div>

                            <div className="co-field">
                              <label className="co-label">Last name</label>
                              <input
                                className="co-input"
                                value={form.lastname}
                                onChange={(e) => setForm((p) => ({ ...p, lastname: e.target.value }))}
                                placeholder="Enter last name"
                                required
                              />
                            </div>
                          </div>

                          <div className="co-appGrid2">
                            <div className="co-field">
                              <label className="co-label">Phone No</label>
                              <input
                                className="co-input"
                                value={form.phone}
                                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                                placeholder="+256 7xx xxx xxx"
                                required
                              />
                            </div>

                            <div className="co-field">
                              <label className="co-label">Email Address</label>
                              <input
                                className="co-input"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                                placeholder="Enter Email Address"
                                required
                              />
                            </div>
                          </div>
                        </>
                      )}

                      <div className="co-sectionHead" style={{ marginTop: 6 }}>
                        <div className="co-sectionTitle">Delivery method</div>
                      </div>

                      <div className="co-mMethodList">
                        <label className={`co-mMethodItem ${!isPickup ? "selected" : ""}`}>
                          <div className="co-mMethodText">
                            <strong>Delivery</strong>
                          </div>
                          <input
                            type="radio"
                            name="delivm"
                            value="delivery"
                            checked={!isPickup}
                            onChange={(e) => {
                              const next = e.target.value;
                              setForm((p) => ({ ...p, deliveryMethod: next }));
                              setEditDelivery(true);
                            }}
                          />
                        </label>

                        <label className={`co-mMethodItem ${isPickup ? "selected" : ""}`}>
                          <div className="co-mMethodText">
                            <strong>Pick Up Station <span>( Free )</span> </strong>
                            
                          </div>
                          <input
                            type="radio"
                            name="delivm"
                            value="pickup"
                            checked={isPickup}
                            onChange={(e) => {
                              const next = e.target.value;
                              setForm((p) => ({ ...p, deliveryMethod: next }));
                              setEditDelivery(true);
                            }}
                          />
                        </label>
                      </div>

                      {isDeliveryFilled && !editDelivery ? (
                        <div className="co-summaryCard">
                          <div className="co-summaryTop">
                            <div className="co-summaryTitle">{isPickup ? "Pick up details" : "Delivery details"}</div>
                            <button
                              type="button"
                              className="edit"
                              onClick={() => setEditDelivery(true)}
                              style={{ background: "transparent", cursor: "pointer" }}
                            >
                              Edit
                            </button>
                          </div>

                          <div className="co-summaryBody">
                            <div>
                              <strong>{isPickup ? "Pick up:" : "Delivery:"}</strong> {deliveryLine || "—"}
                            </div>

                            {!isPickup ? (
                              <div className="co-checkRow" style={{ marginTop: 10 }}>
                                <label className="co-checkBox">
                                  <input
                                    type="checkbox"
                                    checked={saveDelivery}
                                    onChange={(e) => setSaveDelivery(e.target.checked)}
                                  />
                                  <span>Save this address for next time</span>
                                </label>
                              </div>
                            ) : (
                              <div style={{ marginTop: 10, fontSize: 13, color: "#0a7", fontWeight: 800 }}>
                                Shipping is FREE for pick up.
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="co-sectionHead">
                            <div className="co-sectionTitle">{isPickup ? "Pick up details" : "Delivery details"}</div>

                            {isDeliveryFilled ? (
                              <button
                                type="button"
                                className="edit"
                                onClick={() => setEditDelivery(false)}
                                style={{ background: "transparent", cursor: "pointer" }}
                              >
                                Hide
                              </button>
                            ) : null}
                          </div>

                          {isPickup ? (
                            <div className="co-field">
                              <label className="co-label">Pick Up Station</label>
                              <select
                                className="co-input co-select"
                                value={form.pickupStation}
                                onChange={(e) => setForm((p) => ({ ...p, pickupStation: e.target.value }))}
                                onFocus={onDeliveryFocus}
                                onBlur={onDeliveryBlur}
                                required
                              >
                                {PICKUP_STATIONS.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <>
                              <div className="co-field">
                                <label className="co-label">Delivery Address</label>
                                <input
                                  className="co-input"
                                  value={form.address}
                                  onFocus={onDeliveryFocus}
                                  onBlur={onDeliveryBlur}
                                  onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                                  placeholder="Enter Delivery Address"
                                  required
                                />
                              </div>

                              <div className="co-appGrid2">
                                <div className="co-field">
                                  <label className="co-label">Choose your location</label>
                                  <select
                                    className="co-input co-select"
                                    value={form.region}
                                    onFocus={onDeliveryFocus}
                                    onBlur={onDeliveryBlur}
                                    onChange={(e) => {
                                      const nextRegion = e.target.value;
                                      setEditDelivery(true);
                                      setForm((p) => ({
                                        ...p,
                                        region: nextRegion,
                                        subRegion: "",
                                      }));
                                    }}
                                    required
                                  >
                                    <option value="">Select</option>
                                    {DELIVERY_REGIONS.map((r) => (
                                      <option key={r} value={r}>
                                        {r}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <div className="co-field">
                                  <label className="co-label">Sub region</label>
                                  <select
                                    className="co-input co-select"
                                    value={form.subRegion}
                                    onFocus={onDeliveryFocus}
                                    onBlur={onDeliveryBlur}
                                    onChange={(e) => setForm((p) => ({ ...p, subRegion: e.target.value }))}
                                    disabled={!form.region}
                                    required={subRegions.length > 0}
                                  >
                                    <option value="">{!form.region ? "Select location first" : "Select"}</option>
                                    {subRegions.map((s) => (
                                      <option key={s} value={s}>
                                        {s}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              <div className="co-checkRow">
                                <label className="co-checkBox">
                                  <input
                                    type="checkbox"
                                    checked={saveDelivery}
                                    onChange={(e) => setSaveDelivery(e.target.checked)}
                                  />
                                  <span>Save this address for next time</span>
                                </label>
                              </div>
                            </>
                          )}
                        </>
                      )}

                      <div className="co-field">
                        <label className="co-label">
                          Note <span className="co-opt">(Optional)</span>
                        </label>
                        <textarea
                          className="co-textarea"
                          value={form.note}
                          onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                          placeholder="Any special instructions..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>

                 

                  <button
                    type="button"
                    className="co-mPrimaryBtn"
                    onClick={onMobileNext}
                    disabled={placingRef.current || isLoading || !items.length}
                  >
                    CONTINUE TO PAYMENT
                  </button>
                </>
              )}

              {mobileStep === 2 && (
                <>
                  <div className="co-mSectionTitle">Please choose your payment method</div>

                  <div className="co-mPayList">
                    <label className={`co-mPayItem ${form.paymentMethod === "dpo" ? "selected" : ""}`}>
                      <div className="co-mPayLeft">
                        <div className="co-mPayLogo co-mPayLogo--master">DPO</div>
                        <span>Mobile Money / Bank</span>
                      </div>

                      <div className="co-mPayRight">
                        <input
                          type="radio"
                          name="pay"
                          value="dpo"
                          checked={form.paymentMethod === "dpo"}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, paymentMethod: e.target.value }))
                          }
                        />
                      </div>
                    </label>

                    <label className={`co-mPayItem ${form.paymentMethod === "cashOnDelivery" ? "selected" : ""}`}>
                      <div className="co-mPayLeft">
                        <div className="co-mPayLogo co-mPayLogo--master">COD</div>
                        <span>Cash on Delivery</span>
                      </div>

                      <div className="co-mPayRight">
                        <input
                          type="radio"
                          name="pay"
                          value="cashOnDelivery"
                          checked={form.paymentMethod === "cashOnDelivery"}
                          disabled={!codAllowed}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, paymentMethod: e.target.value }))
                          }
                        />
                      </div>
                    </label>
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 13,
                      color: "#888",
                      lineHeight: 1.5,
                    }}
                  >
                   
                  </div>

                  <button
                    type="button"
                    className="co-mPrimaryBtn"
                    onClick={onMobileNext}
                    disabled={placingRef.current || isLoading || !items.length}
                  >
                    PLACE ORDER
                  </button>
                </>
              )}

              {mobileStep === 3 && (
                <>
                  <div className="co-mSectionTitle">Shipping address</div>

                  <div className="co-mSummaryCard">
                    <div className="co-mSummaryTop">
                      <div>
                        <div className="co-mSummaryName">{fullName || "—"}</div>
                        <div className="co-mSummaryAddr">
                          {isPickup ? form.pickupStation || "—" : form.address || "—"}
                        </div>
                        <div className="co-mSummaryAddr">
                          {isPickup
                            ? "Pick Up Station"
                            : [form.region, form.subRegion].filter(Boolean).join(", ") || "—"}
                        </div>
                        <div className="co-mSummaryAddr">{form.email || "—"}</div>
                        <div className="co-mSummaryAddr">{form.phone || "—"}</div>
                      </div>

                      <button type="button" className="co-mTextBtn" onClick={() => setMobileStep(1)}>
                        Edit
                      </button>
                    </div>
                  </div>

                  <div className="co-mSectionTitle">Delivery method</div>

                  <div className="co-mDeliveryOptions">
                    <button
                      type="button"
                      className={`co-mShipCard ${!isPickup ? "selected" : ""}`}
                      onClick={() => setMobileStep(1)}
                    >
                      <div className="co-mShipBrand">Delivery</div>
                      <div className="co-mShipDays">{isPickup ? "Not selected" : formatUGX(shipping)}</div>
                    </button>

                    <button
                      type="button"
                      className={`co-mShipCard ${isPickup ? "selected" : ""}`}
                      onClick={() => setMobileStep(1)}
                    >
                      <div className="co-mShipBrand">Pick Up</div>
                      <div className="co-mShipDays">FREE</div>
                    </button>
                  </div>

                  <div className="co-mSectionTitle">Please choose your payment method</div>

                  <div
                    className="co-mSummaryPayRow"
                    onClick={() => setMobileStep(2)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setMobileStep(2);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="co-mPayLeft">
                      {form.paymentMethod === "cashOnDelivery" ? (
                        <div className="co-mPayLogo co-mPayLogo--master">COD</div>
                      ) : (
                        <img
                          src={getPaymentImage(form.paymentMethod)}
                          alt={paymentLabel}
                          style={{
                            width: 56,
                            height: 40,
                            objectFit: "contain",
                            borderRadius: 8,
                            background: "#fff",
                            border: "1px solid #eee",
                            padding: 2,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <span>{paymentLabel}</span>
                    </div>
                    <span className="co-mChevron">›</span>
                  </div>

                  <div className="co-mAmountRows">
                    <div className="co-mAmountRow">
                      <span>Delivery:</span>
                      <strong>{formatUGX(shipping)}</strong>
                    </div>

                    <div className="co-mAmountRow">
                      <span>Total order:</span>
                      <strong>{formatUGX(total)}</strong>
                    </div>
                  </div>

                  {setupFeeTotal > 0 ? (
                    <div className="co-mMiniMeta">Setup fee included: {formatUGX(setupFeeTotal)}</div>
                  ) : null}

                  {!items.length ? <div className="co-appWarn">Your cart is empty. Add items to proceed.</div> : null}

                  <button
                    type="button"
                    className="co-mPrimaryBtn"
                    onClick={onMobileNext}
                    disabled={placingRef.current || isLoading || !items.length}
                  >
                    CONFIRM PAYMENT
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ======================================================
          ✅ DESKTOP UI
      ======================================================= */}
      <div className="co-desktop">
        <div className="check-hero">
          <h1 className="ct-title">Place your order</h1>
          <p className="ct-bread">
            Home <span>/</span> Checkout
          </p>
        </div>

        <div className="co-shell">
          <section className="co-left">
            <div className="co-steps" aria-label="Checkout steps">
              <div className={`co-step ${step === 1 ? "active" : step > 1 ? "done" : ""}`}>
                <span className="co-stepNum">1</span>
                <span className="co-stepLbl">Information</span>
              </div>

              <div className={`co-step ${step === 2 ? "active" : step > 2 ? "done" : ""}`}>
                <span className="co-stepNum">2</span>
                <span className="co-stepLbl">Payment</span>
              </div>

              <div className={`co-step ${step === 3 ? "active" : ""}`}>
                <span className="co-stepNum">3</span>
                <span className="co-stepLbl">Complete Order</span>
              </div>
            </div>

            {showSkeleton ? (
              <FormSkeleton />
            ) : (
              <form className="co-form" onSubmit={onProceed}>
                {step === 1 && (
                  <>
                    {isIdentityFilled && !editIdentity ? (
                      <div className="co-summaryCard">
                        <div className="co-summaryTop">
                          <div className="co-summaryTitle">Contact details</div>
                          <button
                            type="button"
                            className="edit"
                            onClick={() => setEditIdentity(true)}
                            style={{ background: "transparent", cursor: "pointer" }}
                          >
                            Edit
                          </button>
                        </div>

                        <div className="co-summaryBody">
                          <div>
                            <strong>Name:</strong> {fullName || "—"}
                          </div>
                          <div>
                            <strong>Phone:</strong> {form.phone || "—"}
                          </div>
                          <div>
                            <strong>Email:</strong> {form.email || "—"}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="co-sectionHead">
                          <div className="co-sectionTitle">Contact details</div>

                          {isIdentityFilled ? (
                            <button
                              type="button"
                              className="edit"
                              onClick={() => setEditIdentity(false)}
                              style={{ background: "transparent", cursor: "pointer" }}
                            >
                              Hide
                            </button>
                          ) : null}
                        </div>

                        <div className="co-grid2">
                          <div className="co-field">
                            <label className="co-label">First name</label>
                            <input
                              className="co-input"
                              value={form.firstname}
                              onChange={(e) => setForm((p) => ({ ...p, firstname: e.target.value }))}
                              placeholder="Enter first name"
                              required
                            />
                          </div>

                          <div className="co-field">
                            <label className="co-label">Last name</label>
                            <input
                              className="co-input"
                              value={form.lastname}
                              onChange={(e) => setForm((p) => ({ ...p, lastname: e.target.value }))}
                              placeholder="Enter last name"
                              required
                            />
                          </div>
                        </div>

                        <div className="co-grid2">
                          <div className="co-field">
                            <label className="co-label">Phone No</label>
                            <input
                              className="co-input"
                              value={form.phone}
                              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                              placeholder="+256 7xx xxx xxx"
                              required
                            />
                          </div>

                          <div className="co-field">
                            <label className="co-label">Email Address</label>
                            <input
                              className="co-input"
                              type="email"
                              value={form.email}
                              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                              placeholder="Enter Email Address"
                              required
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="co-sectionHead" style={{ marginTop: 6 }}>
                      <div className="co-sectionTitle">Delivery method</div>
                    </div>

                    <div className="co-payMock" style={{ marginBottom: 10 }}>
                      <label className="co-payRadio" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="delivery"
                          checked={!isPickup}
                          onChange={(e) => {
                            const next = e.target.value;
                            setForm((p) => ({ ...p, deliveryMethod: next }));
                            setEditDelivery(true);
                          }}
                        />
                        <span>Delivery</span>
                      </label>

                      <label className="co-payRadio" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="pickup"
                          checked={isPickup}
                          onChange={(e) => {
                            const next = e.target.value;
                            setForm((p) => ({ ...p, deliveryMethod: next }));
                            setEditDelivery(true);
                          }}
                        />
                        <span>
                          Pick Up Station <span style={{ color: "#0a7", fontWeight: 900 }}>(Free)</span>
                        </span>
                      </label>
                    </div>

                    {isDeliveryFilled && !editDelivery ? (
                      <div className="co-summaryCard">
                        <div className="co-summaryTop">
                          <div className="co-summaryTitle">{isPickup ? "Pick up details" : "Delivery details"}</div>

                          <button
                            type="button"
                            className="edit"
                            onClick={() => setEditDelivery(true)}
                            style={{ background: "transparent", cursor: "pointer" }}
                          >
                            Edit
                          </button>
                        </div>

                        <div className="co-summaryBody">
                          <div>
                            <strong>{isPickup ? "Pick up:" : "Delivery:"}</strong> {deliveryLine || "—"}
                          </div>

                          {!isPickup ? (
                            <div className="co-checkRow" style={{ marginTop: 10 }}>
                              <label className="co-checkBox">
                                <input
                                  type="checkbox"
                                  checked={saveDelivery}
                                  onChange={(e) => setSaveDelivery(e.target.checked)}
                                />
                                <span>Save this address for next time</span>
                              </label>
                            </div>
                          ) : (
                            <div style={{ marginTop: 10, fontSize: 13, color: "#0a7", fontWeight: 800 }}>
                              Shipping is FREE for pick up.
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="co-sectionHead">
                          <div className="co-sectionTitle">{isPickup ? "Pick up details" : "Delivery details"}</div>

                          {isDeliveryFilled ? (
                            <button
                              type="button"
                              className="edit"
                              onClick={() => setEditDelivery(false)}
                              style={{ background: "transparent", cursor: "pointer" }}
                            >
                              Hide
                            </button>
                          ) : null}
                        </div>

                        {isPickup ? (
                          <div className="co-field">
                            <label className="co-label">
                              Pick Up Station <span className="co-opt">(Free)</span>
                            </label>
                            <select
                              className="co-input co-select"
                              value={form.pickupStation}
                              onChange={(e) => setForm((p) => ({ ...p, pickupStation: e.target.value }))}
                              onFocus={onDeliveryFocus}
                              onBlur={onDeliveryBlur}
                              required
                            >
                              {PICKUP_STATIONS.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <>
                            <div className="co-field">
                              <label className="co-label">Delivery Address</label>
                              <input
                                className="co-input"
                                value={form.address}
                                onFocus={onDeliveryFocus}
                                onBlur={onDeliveryBlur}
                                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                                placeholder="Enter Delivery Address"
                                required
                              />
                            </div>

                            <div className="co-grid2">
                              <div className="co-field">
                                <label className="co-label">Choose your location</label>
                                <select
                                  className="co-input co-select"
                                  value={form.region}
                                  onFocus={onDeliveryFocus}
                                  onBlur={onDeliveryBlur}
                                  onChange={(e) => {
                                    const nextRegion = e.target.value;

                                    setEditDelivery(true);

                                    setForm((p) => ({
                                      ...p,
                                      region: nextRegion,
                                      subRegion: "",
                                    }));
                                  }}
                                  required
                                >
                                  <option value="">Select</option>
                                  {DELIVERY_REGIONS.map((r) => (
                                    <option key={r} value={r}>
                                      {r}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="co-field">
                                <label className="co-label">Sub region</label>
                                <select
                                  className="co-input co-select"
                                  value={form.subRegion}
                                  onFocus={onDeliveryFocus}
                                  onBlur={onDeliveryBlur}
                                  onChange={(e) => setForm((p) => ({ ...p, subRegion: e.target.value }))}
                                  disabled={!form.region}
                                  required={subRegions.length > 0}
                                >
                                  <option value="">{!form.region ? "Select location first" : "Select"}</option>
                                  {subRegions.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <div className="co-checkRow">
                              <label className="co-checkBox">
                                <input
                                  type="checkbox"
                                  checked={saveDelivery}
                                  onChange={(e) => setSaveDelivery(e.target.checked)}
                                />
                                <span>Save this address for next time</span>
                              </label>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    <div className="co-field">
                      <label className="co-label">
                        Note <span className="co-opt">(Optional)</span>
                      </label>
                      <textarea
                        className="co-textarea"
                        value={form.note}
                        onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                        placeholder="Any special instructions..."
                        rows={5}
                      />
                    </div>
                  </>
                )}

                {step === 2 && (
                  <div className="co-payDesktop">
                    <div className="co-payHeader">
                      <div>
                        <h3 className="co-payTitle">Choose your payment method</h3>
                        <p className="co-payText">
                          Select how you want to pay for this order and review your checkout totals before confirming.
                        </p>
                      </div>

                      <div className="co-payBadge">{paymentLabel}</div>
                    </div>

                    <div className="co-payLayout">
                      <div className="co-payMethods">
                        <label className={`co-payOption ${form.paymentMethod === "dpo" ? "selected" : ""}`}>
                          <input
                            type="radio"
                            name="pay"
                            value="dpo"
                            checked={form.paymentMethod === "dpo"}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, paymentMethod: e.target.value }))
                            }
                          />

                          <div className="co-payOptionIcon co-payOptionIcon--cod">DPO</div>

                          <div className="co-payOptionBody">
                            <div className="co-payOptionTitle">Mobile Money / Bank</div>
                            <div className="co-payOptionSub">
                              Pay by card, MTN MoMo, Airtel Money, or other supported DPO methods.
                            </div>
                          </div>

                          <div className="co-payTick" />
                        </label>

                        <label className={`co-payOption ${form.paymentMethod === "cashOnDelivery" ? "selected" : ""}`}>
                          <input
                            type="radio"
                            name="pay"
                            value="cashOnDelivery"
                            checked={form.paymentMethod === "cashOnDelivery"}
                            disabled={!codAllowed}
                            onChange={(e) =>
                              setForm((p) => ({ ...p, paymentMethod: e.target.value }))
                            }
                          />

                          <div className="co-payOptionIcon co-payOptionIcon--cod">COD</div>

                          <div className="co-payOptionBody">
                            <div className="co-payOptionTitle">Cash on Delivery</div>
                          </div>

                          <div className="co-payTick" />
                        </label>
                      </div>

                      <div className="co-paySummaryCard">
                        <div className="co-paySummaryTop">
                          <div className="co-paySummaryTitle">Payment summary</div>
                          <div className="co-paySummaryMethod">{paymentLabel}</div>
                        </div>

                        <div className="co-paySummaryGrid">
                          <div className="co-payStat">
                            <span>Items total</span>
                            <strong>{formatUGX(itemsTotal)}</strong>
                          </div>

                          <div className="co-payStat">
                            <span>Shipping</span>
                            <strong>{formatUGX(shipping)}</strong>
                          </div>

                          {setupFeeTotal > 0 ? (
                            <div className="co-payStat">
                              <span>Setup fee</span>
                              <strong>{formatUGX(setupFeeTotal)}</strong>
                            </div>
                          ) : null}

                          <div className="co-payStat co-payStat--total">
                            <span>Total to pay</span>
                            <strong>{formatUGX(total)}</strong>
                          </div>
                        </div>

                        <div className="co-payMethodMeta">
                          <div className="co-payMetaRow">
                            <span>Delivery</span>
                            <b>{isPickup ? "Pick Up Station" : "Home / Office Delivery"}</b>
                          </div>

                          <div className="co-payMetaRow">
                            <span>Destination</span>
                            <b>{deliveryLine || "—"}</b>
                          </div>

                          <div className="co-payMetaRow">
                            <span>Customer</span>
                            <b>{fullName || "—"}</b>
                          </div>
                        </div>

                        
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="co-completeWrap">
                    <div className="co-doneBox">
                      <h3 className="co-doneTitle">Complete Order</h3>
                      <p className="co-doneText">Confirm your details below, then click finish to place the order.</p>

                      <div className="co-completeGrid">
                        <div className="co-completeCard">
                          <div className="co-completeCardTitle">Customer details</div>
                          <div className="co-completeList">
                            <div>
                              <strong>Name:</strong> {fullName || "—"}
                            </div>
                            <div>
                              <strong>Phone:</strong> {form.phone || "—"}
                            </div>
                            <div>
                              <strong>Email:</strong> {form.email || "—"}
                            </div>
                          </div>
                        </div>

                        <div className="co-completeCard">
                          <div className="co-completeCardTitle">Delivery & payment</div>
                          <div className="co-completeList">
                            <div>
                              <strong>{isPickup ? "Pick up:" : "Address:"}</strong> {deliveryLine || "—"}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <strong>Payment method:</strong>
                              {form.paymentMethod === "cashOnDelivery" ? (
                                <span>{paymentLabel}</span>
                              ) : (
                                <>
                                  <img
                                    src={getPaymentImage(form.paymentMethod)}
                                    alt={paymentLabel}
                                    style={{
                                      width: 70,
                                      height: 70,
                                      objectFit: "contain",
                                      borderRadius: 8,
                                      background: "#fff",
                                      
                                    }}
                                  />
                                  <span>{paymentLabel}</span>
                                </>
                              )}
                            </div>

                            {form.note ? (
                              <div>
                                <strong>Note:</strong> {form.note}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="co-completeCard co-completeCard--full">
                          <div className="co-completeCardTitle">Order totals</div>
                          <div className="co-completeTotals">
                            <div className="co-completeRow">
                              <span>Items</span>
                              <strong>{formatUGX(itemsTotal)}</strong>
                            </div>

                            {setupFeeTotal > 0 ? (
                              <div className="co-completeRow">
                                <span>Setup fee</span>
                                <strong>{formatUGX(setupFeeTotal)}</strong>
                              </div>
                            ) : null}

                            <div className="co-completeRow">
                              <span>Shipping</span>
                              <strong>
                                {formatUGX(shipping)}{" "}
                                {isPickup ? <span style={{ color: "#0a7" }}>(FREE pick up)</span> : null}
                              </strong>
                            </div>

                            <div className="co-completeDivider" />

                            <div className="co-completeRow co-completeRow--total">
                              <span>Total</span>
                              <strong>{formatUGX(total)}</strong>
                            </div>
                          </div>

                          <div className="co-completeFoot">
                              Finish will create your pending order, then redirect you to secure payment.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                  <button type="button" className="co-proceed" onClick={goBackStep} style={{ opacity: 0.9 }}>
                    {step === 1 ? "Back" : "Previous"}
                  </button>

                  <button type="submit" className="co-proceed" disabled={placingRef.current || isLoading}>
                    {step === 1 ? "Proceed to Payment" : step === 2 ? "Proceed to Confirm" : "Finish"}
                  </button>
                </div>
              </form>
            )}
          </section>

          <aside className="co-right">
            <div className="co-panel">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <h3 className="co-rTitle">Your Cart</h3>

                <button
                  className="edit"
                  type="button"
                  onClick={() => navigate("/cart")}
                  style={{ background: "transparent", cursor: "pointer" }}
                >
                  Edit Cart
                </button>
              </div>

              <div className="co-cartList">
                {showSkeleton ? (
                  <>
                    {[1, 2, 3].map((i) => (
                      <CartItemSkeleton k={`sk-${i}`} key={`sk-${i}`} />
                    ))}
                    <div className="co-skelBtnWide" />
                  </>
                ) : items.length ? (
                  items.map((p) => {
                    const printSideLabel =
                      p.printSide === "oneSide" ? "One-side" : p.printSide === "twoSide" ? "Two-side" : "";

                    const printDiscountApplied =
                      p.printDiscountMinQty && Number(p.qty || 1) >= Number(p.printDiscountMinQty);

                    const lineTotal = (Number(p.unitPrice) || 0) * (Number(p.qty) || 0);

                    return (
                      <div key={p.cartItemId} className="co-cartItem">
                        <div className="co-cartThumb">
                          <img src={p.img || product1} alt={p.name} />
                        </div>

                        <div className="co-cartInfo">
                          <div className="co-cartName">{p.name}</div>

                          {p.color || p.size ? (
                            <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                              {p.color ? <>Color: {p.color}</> : null}
                              {p.color && p.size ? " • " : null}
                              {p.size ? <>Size: {p.size}</> : null}
                            </div>
                          ) : null}

                          {p.hasPrintPricing && (p.printPricingTitle || p.printSide) ? (
                            <div style={{ marginTop: 6, fontSize: 12, color: "#777", lineHeight: 1.35 }}>
                              <div>
                                Printing: <strong>{p.printPricingTitle || "—"}</strong>
                                {printSideLabel ? ` • ${printSideLabel}` : ""}
                              </div>

                              {p.printUnitPrice > 0 ? (
                                <div>
                                  Print unit: <strong>{formatUGX(p.printUnitPrice)}</strong>
                                  {p.printDiscountMinQty ? (
                                    <>
                                      {" "}
                                      • Disc from ≥ {Number(p.printDiscountMinQty)}
                                      {printDiscountApplied ? <span style={{ color: "#0a7" }}> (applied)</span> : null}
                                    </>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          ) : null}

                          <div className="co-cartPrice">{formatUGX(lineTotal)}</div>
                        </div>

                        <div className="co-cartControls">
                          <button
                            type="button"
                            className="co-qBtn"
                            onClick={() => decQty(p.cartItemId, p.qty)}
                            aria-label="Decrease"
                          >
                            –
                          </button>

                          <input
                            className="co-qInput"
                            value={p.qty}
                            onChange={(e) => setQty(p.cartItemId, e.target.value)}
                            inputMode="numeric"
                            aria-label="Quantity"
                          />

                          <button
                            type="button"
                            className="co-qBtn"
                            onClick={() => incQty(p.cartItemId, p.qty)}
                            aria-label="Increase"
                          >
                            +
                          </button>

                          <button
                            type="button"
                            className="co-xBtn"
                            onClick={() => removeItem(p.cartItemId)}
                            aria-label="Remove"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="co-cartEmpty">Your cart is empty.</div>
                )}

                {!showSkeleton && items.length ? (
                  <button className="clear" type="button" onClick={clearCart}>
                    Clear Cart
                  </button>
                ) : null}
              </div>
            </div>

            <div className="co-panel co-panel--pd">
              <h3 className="co-rTitle">Price Details</h3>

              {showSkeleton ? (
                <PriceSkeleton />
              ) : (
                <div className="co-priceRows">
                  <div className="co-row">
                    <span>Items ({itemsCount})</span>
                    <strong>{formatUGX(itemsTotal)}</strong>
                  </div>

                  {setupFeeTotal > 0 ? (
                    <div className="co-row">
                      <span>Setup fee (once)</span>
                      <strong>{formatUGX(setupFeeTotal)}</strong>
                    </div>
                  ) : null}

                  <div className="co-row">
                    <span>
                      Shipping{" "}
                      <span style={{ color: "#888" }}>
                        ({isPickup ? `Pick Up • ${form.pickupStation || "—"}` : form.region || "—"}
                        {!isPickup && form.subRegion ? ` • ${form.subRegion}` : ""})
                      </span>
                    </span>
                    <strong>{formatUGX(shipping)}</strong>
                  </div>

                  <div className="co-divider" />

                  <div className="co-row co-row--total">
                    <span>Total</span>
                    <strong>{formatUGX(total)}</strong>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Checkout;